// @ts-check
import classNames from 'classnames';
import { GatsbyImage as GatsbyPluginImage } from 'gatsby-plugin-image';
import React from 'react';
import { useContentStackProps } from '~/contentstack';
import { getImageDisplayOptions } from '~/templates/utils/ImageHelper';
import BynderSimpleImage from '../BynderSimpleImage';
import { cleanDollarFrom } from './cleanDollar';

/**
 * @typedef GatsbyImageProps
 * @property {import('~/templates/CSTypes').BynderImageOptions} option
 * @property {string} [keyOfChildImageSharp='gatsbyImageData']
 */
/** @type {import('~/types/React').ReactFC<GatsbyImageProps, HTMLImageElement>} */
const GatsbyImage = ({ option, keyOfChildImageSharp = 'gatsbyImageData', ...rest }) => {
	const initialOption = cleanDollarFrom(option);

	const [model, modelWith$] = useContentStackProps({
		staticModel: initialOption,
		contentType: 'bynder_image',
		contentUid: initialOption?.uid,
	});

	const imageOptions = getImageDisplayOptions(model);

	if (!imageOptions?.url || !model) {
		return null;
	}

	// If there is no remoteToLocalImage, then we are in live-preview. Show a preview.
	if (!model.remoteToLocalImage) {
		return <BynderSimpleImage {...rest} type="img" isLivePreviewImage={true} option={modelWith$} />;
	}

	return (
		// @ts-expect-error - The types aren't quite the same as ours - but functionally identical.
		<GatsbyPluginImage
			{...rest}
			{...modelWith$?.$?.uid}
			className={classNames(rest.className, 'ozk-gatsby-image')}
			alt={imageOptions.alt_text}
			image={model.remoteToLocalImage.childImageSharp[keyOfChildImageSharp]}
			objectPosition={imageOptions.position}
			objectFit={imageOptions.fit}
			style={{
				...rest.style,

				// Add max-* as styles
				maxWidth: model.max_width,
				maxHeight: model.max_height,
			}}
		/>
	);
};

export default GatsbyImage;
